import React, { useState } from 'react';
import styled from 'styled-components';
import { Controller, useFieldArray } from 'react-hook-form';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { CheckboxOld, Button } from 'shared/components';
import { VisuallyHidden } from 'shared/components/visually-hidden';
import { SuperAdminContent } from 'src/components/super-admin-content';
import { MenuCustomizationLayout } from '../components/menu-customization-layout';

import { useExtensionsForm, UseExtensionsFormProps } from './use-extensions-form';

import { Heading, HelperText, Input, Inputs, Label, SubText } from '../customize.styles';

export function ExtensionsForm(props: Omit<UseExtensionsFormProps, 'applyToAll'>): JSX.Element {
  const [applyToAll, setApplyToAll] = useState(false);
  const flags = useFlags();
  const showBuilderKeyField = flags['growth.ecomm.builder-configuration.rollout'] ?? false;
  const { control, handlePublish, handleReset, isDirty } = useExtensionsForm({
    ...props,
    applyToAll,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'theme.extensionData',
  });

  return (
    <MenuCustomizationLayout
      headerText='Extensions'
      subHeaderText='Configure remote extensions for your menu.'
      isDirty={isDirty}
      handlePublish={() => void handlePublish()}
      handleReset={handleReset}
    >
      <Inputs>
        <Input>
          <Label htmlFor='manifestUrl'>Module URL</Label>
          <StyledHelperText>Enter the URL for the module manifest</StyledHelperText>
          <Controller
            name='theme.manifestUrl'
            control={control}
            render={({ onChange, value }) => (
              <ExtensionInput
                type='text'
                id='manifestUrl'
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder='https://example.com/manifest.json'
              />
            )}
          />
        </Input>

        <Input>
          <Label htmlFor='scope'>Scope (Module Name)</Label>
          <StyledHelperText>Enter the scope of the module</StyledHelperText>
          <Controller
            name='theme.scope'
            control={control}
            render={({ onChange, value }) => (
              <ExtensionInput
                type='text'
                id='scope'
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder='@dutchie/customer-themes'
              />
            )}
          />
        </Input>

        <Input>
          <Label htmlFor='modulePath'>Module Path</Label>
          <StyledHelperText>Enter the path for the module</StyledHelperText>
          <Controller
            name='theme.modulePath'
            control={control}
            render={({ onChange, value }) => (
              <ExtensionInput
                type='text'
                id='modulePath'
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder='themes/theme-name'
              />
            )}
          />
        </Input>
      </Inputs>

      <Heading>Extension Data</Heading>
      <SubText>Provide custom key/value data that will be available to your extensions.</SubText>

      <Inputs>
        {fields.length > 0 && (
          <Table>
            <thead>
              <tr>
                <Th scope='col'>Key</Th>
                <Th scope='col'>Value</Th>
                <th scope='col'>
                  <VisuallyHidden>Remove</VisuallyHidden>
                </th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field, index) => (
                <tr key={field.id}>
                  <td>
                    <VisuallyHidden>
                      <Label htmlFor={`extensionData-${index}-key`}>Key</Label>
                    </VisuallyHidden>
                    <Controller
                      name={`theme.extensionData.${index}.key`}
                      control={control}
                      render={({ onChange, value }) => (
                        <ExtensionInput
                          type='text'
                          id={`extensionData-${index}-key`}
                          value={value}
                          onChange={(e) => onChange(e.target.value)}
                        />
                      )}
                    />
                  </td>
                  <td>
                    <VisuallyHidden>
                      <Label htmlFor={`extensionData-${index}-value`}>Value</Label>
                    </VisuallyHidden>
                    <Controller
                      name={`theme.extensionData.${index}.value`}
                      control={control}
                      render={({ onChange, value }) => (
                        <ExtensionInput
                          type='text'
                          id={`extensionData-${index}-value`}
                          value={value}
                          onChange={(e) => onChange(e.target.value)}
                        />
                      )}
                    />
                  </td>
                  <td>
                    <RemoveButton link onClick={() => remove(index)}>
                      Remove
                    </RemoveButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <AddButton link onClick={() => append({ key: '', value: '' })}>
          + Add Extension Data
        </AddButton>
      </Inputs>

      {showBuilderKeyField && (
        <SuperAdminContent>
          <div>
            <Heading>Builder API Key</Heading>
            <SubText>Enter the Builder.io API key for accessing custom content.</SubText>
            <Inputs>
              <Input>
                <Label htmlFor='builderKey'>Builder API Key</Label>
                <StyledHelperText>This key is used to fetch custom content from Builder.io</StyledHelperText>
                <Controller
                  name='builderKey'
                  control={control}
                  render={({ onChange, value }) => (
                    <ExtensionInput
                      type='text'
                      id='builderKey'
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                      placeholder='Enter Builder API Key'
                    />
                  )}
                />
              </Input>
            </Inputs>
          </div>
        </SuperAdminContent>
      )}

      <Divider />

      <Inputs>
        <Input>
          <CheckboxOld
            id='applyToAll'
            label='Apply settings to all locations in chain'
            name='applyToAll'
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setApplyToAll(e.target.checked);
            }}
          />
        </Input>
      </Inputs>
    </MenuCustomizationLayout>
  );
}

const ExtensionInput = styled.input`
  border: 1px solid #d3d8de;
  border-radius: 4px;
  padding: 8px;
  width: 100%;
`;

const StyledHelperText = styled(HelperText)`
  margin: 0 0 4px;
`;

const Table = styled.table`
  text-align: left;
  width: 100%;
`;

const Th = styled.th`
  width: 40%;
`;

const RemoveButton = styled(Button)`
  color: #e53935;
  margin-left: 4px;
  padding: 0;

  &:hover,
  &:active,
  &:focus,
  &:focus:not(:hover) {
    background: none;
    color: #e53935;
  }
`;

const AddButton = styled(Button)`
  padding: 0;

  &:hover,
  &:active,
  &:focus,
  &:focus:not(:hover) {
    background: none;
    color: ${({ theme }) => theme.colors.primaryBlue};
  }
`;

const Divider = styled.div`
  border-bottom: 1px solid #d3d8de;
  margin-bottom: 28px;
  width: 100%;
`;
